import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ApiError } from '@/lib/api'
import {
  languageCreateSchema,
  useCreateLanguage,
  useSaveLanguage,
  type Language,
  type LanguageCreateInput,
} from '@/features/i18n/api'

/**
 * Add or edit one dictionary row.
 *
 * `display_code` is a real field now. It used to be omitted because create
 * derived it from the code and discarded what was sent — but the two disagree
 * for every language whose picker label is a region code rather than a language
 * one: Czech is `cs` and shows CZ, Vietnamese is `vi` and shows VN, Korean is
 * `ko` and shows KR. Deriving it made exactly those unreachable from here.
 *
 * It is what the consumer picker renders, so leaving it blank falls back to the
 * upper-cased code, which is right for the other twenty-three.
 *
 * `code` and `is_baseline` are not editable after creation: the code is a join
 * key that `trending_item_locales` and the frontend locale directories already
 * reference, and the database keeps exactly one baseline.
 */

interface LanguageEditorProps {
  /** Absent for a new language. */
  language?: Language
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function LanguageEditor({ language, open, onOpenChange }: LanguageEditorProps) {
  const isNew = language === undefined
  const createLanguage = useCreateLanguage()
  const saveLanguage = useSaveLanguage()

  // Seeded from the row passed in as a prop rather than filled by an effect:
  // `useForm` captures its defaults on the first render, so seeding later paints
  // empty inputs and then overwrites whatever has been typed.
  const form = useForm<LanguageCreateInput>({
    resolver: standardSchemaResolver(languageCreateSchema),
    defaultValues: {
      code: language?.code ?? '',
      display_code: language?.display_code ?? '',
      display_name: language?.display_name ?? '',
      llm_name: language?.llm_name ?? '',
      is_popular: language?.is_popular ?? false,
      is_rtl: language?.is_rtl ?? false,
      sort_weight: language?.sort_weight ?? 50,
      enabled: language?.enabled ?? true,
    },
  })

  const isBaseline = language?.is_baseline ?? false

  async function onSubmit(values: LanguageCreateInput) {
    try {
      if (isNew) {
        await createLanguage.mutateAsync({
          ...values,
          code: values.code.toLowerCase(),
          // Blank means "derive it", which the backend does from the code.
          display_code: values.display_code.trim().toUpperCase(),
        })
        toast.success(
          `${values.display_name} added. Every existing key reads as untranslated for it and ships English until you run Fill missing.`,
        )
      } else {
        await saveLanguage.mutateAsync({
          id: language.id,
          input: {
            display_code: values.display_code.trim().toUpperCase(),
            display_name: values.display_name,
            llm_name: values.llm_name,
            is_popular: values.is_popular,
            is_rtl: values.is_rtl,
            sort_weight: values.sort_weight,
            enabled: values.enabled,
          },
        })
        toast.success(`${values.display_name} updated.`)
      }
      onOpenChange(false)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not save the language.')
    }
  }

  const saving = createLanguage.isPending || saveLanguage.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add language' : `Edit ${language.display_name}`}</DialogTitle>
          <DialogDescription>
            {isNew
              ? 'It appears in the consumer language picker as soon as it is enabled.'
              : 'Changes reach the consumer app without a client release.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="code">Language code</Label>
              <Input
                id="code"
                {...form.register('code')}
                disabled={!isNew}
                spellCheck={false}
                className="font-mono text-xs"
                placeholder="zh-tw"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {isNew ? (
                  <>
                    Must match the folder in <code>cwc-frontend/i18n/locales</code>. Lowercase.
                  </>
                ) : (
                  'Fixed — the frontend locale folders and trending locales reference it.'
                )}
              </p>
              {form.formState.errors.code && (
                <p className="mt-1 text-xs text-destructive">
                  {form.formState.errors.code.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="display_name">Display name</Label>
              <Input id="display_name" {...form.register('display_name')} placeholder="繁體中文" />
              <p className="mt-1 text-xs text-muted-foreground">
                Shown to users in the picker, so write it in its own language.
              </p>
              {form.formState.errors.display_name && (
                <p className="mt-1 text-xs text-destructive">
                  {form.formState.errors.display_name.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="display_code">Picker label</Label>
            <Input id="display_code" {...form.register('display_code')} placeholder="VN" />
            <p className="mt-1 text-xs text-muted-foreground">
              The short label beside the flag. Often the country rather than the language —{' '}
              <code>vi</code> shows <b>VN</b>, <code>cs</code> shows <b>CZ</b>. Blank uses the code
              upper-cased.
            </p>
            {form.formState.errors.display_code && (
              <p className="mt-1 text-xs text-destructive">
                {form.formState.errors.display_code.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="llm_name">Sent to the model as</Label>
            <Input id="llm_name" {...form.register('llm_name')} placeholder="Traditional Chinese" />
            <p className="mt-1 text-xs text-muted-foreground">
              Spelled out in English — handing a model <code>zh-tw</code> produces noticeably worse
              translations than <code>Traditional Chinese</code>. Blank falls back to the display
              name.
            </p>
          </div>

          <div>
            <Label htmlFor="sort_weight">Picker order</Label>
            <Input
              id="sort_weight"
              type="number"
              inputMode="numeric"
              {...form.register('sort_weight')}
              className="w-32"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Higher appears first. Popular languages are grouped above everything else regardless,
              so this orders within a group. Existing languages use 100 down to 10.
            </p>
            {form.formState.errors.sort_weight && (
              <p className="mt-1 text-xs text-destructive">
                {form.formState.errors.sort_weight.message}
              </p>
            )}
          </div>

          <div className="space-y-2.5 rounded-lg border border-border-strong bg-secondary/50 p-3">
            <label className="flex items-center justify-between gap-3 text-[13px]">
              <span>
                <b>Popular</b>
                <span className="block text-xs text-muted-foreground">
                  Grouped at the top of the picker.
                </span>
              </span>
              <Switch
                checked={form.watch('is_popular')}
                // The baseline is always Popular — English is the fallback every
                // other language resolves to, so burying it in the long tail
                // would be actively unhelpful.
                disabled={isBaseline}
                onCheckedChange={(checked) =>
                  form.setValue('is_popular', checked, { shouldDirty: true })
                }
              />
            </label>

            <label className="flex items-center justify-between gap-3 text-[13px]">
              <span>
                <b>Right to left</b>
                <span className="block text-xs text-muted-foreground">
                  Arabic, Hebrew, Farsi. Flips the translation editor and the app layout.
                </span>
              </span>
              <Switch
                checked={form.watch('is_rtl')}
                onCheckedChange={(checked) => form.setValue('is_rtl', checked, { shouldDirty: true })}
              />
            </label>

            <label className="flex items-center justify-between gap-3 text-[13px]">
              <span>
                <b>Enabled</b>
                <span className="block text-xs text-muted-foreground">
                  Offered to users, and included in every translation run.
                </span>
              </span>
              <Switch
                checked={form.watch('enabled')}
                // The baseline cannot be switched off: it is the source every
                // other language falls back to.
                disabled={isBaseline}
                onCheckedChange={(checked) =>
                  form.setValue('enabled', checked, { shouldDirty: true })
                }
              />
            </label>
          </div>

          {isNew && form.watch('enabled') && (
            <div className="flex items-start gap-2 rounded-md bg-warning-soft p-2.5 text-xs text-[#a9760c]">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <div>
                Enabling it now means users can pick it immediately, and every key will render in
                English until translations exist — which reads as finished copy rather than as
                missing copy. Add it disabled, or run <b>Fill missing</b> straight afterwards.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Add language' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
